import { Pencil } from 'lucide-react-native';
import { useState, type Ref } from 'react';

import { CaseDetails, SimilarCases } from './CaseFacts';
import { ChangedRecently } from './CaseNotices';
import { DECISION_OPTION_CLASS, DecisionCard } from './DecisionCard';
import { PeerSummary } from './PeerSummary';
import { REPLY_DRAFT_TOUR_ZONES } from '../../model/tour';
import type { DraftSelection, ReplyDraft } from '../../model/types';
import { REPLY_DRAFT_COPY } from '../copy';
import { SectionHeading } from '../shared/SectionHeading';
import { TextEntry } from '../shared/TextEntry';
import type { HeadingNode } from '../shared/focusHeading';
import { ReplyDraftTourZone } from '../tour/ReplyDraftTourZone';

import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type DecideStepProps = {
  draft: ReplyDraft;
  isSelecting: boolean;
  onSelect: (selection: DraftSelection) => Promise<boolean>;
  /** Back to the existing draft without rewriting it, when the current option is picked again. */
  onKeep: () => void;
  headingRef?: Ref<HeadingNode>;
};

/**
 * Guidance before draft, read top to bottom: the asker's question with the
 * case under it, then Research, then a few lines saying the choice is only
 * where the draft starts, then the options with "Something else" as their
 * last card. The question leads because it is what the expert answers.
 *
 * Research sits between the case and the options, closed: what changed
 * recently, the expert's own history and peers' answers. It comes before the
 * choice so it is seen while deciding, and stays collapsed so it informs the
 * decision rather than leads it. Each row carries its count, so a recent
 * approval still shows it is there before the expert chooses.
 *
 * Nothing here reads the draft's text, which is how the card stays free of it
 * even with an earlier option's draft cached. Only the chosen option's key is
 * read, to mark it when the expert comes back to reconsider; picking it again
 * returns to that draft rather than writing a new one.
 */
export function DecideStep({ draft, isSelecting, onSelect, onKeep, headingRef }: DecideStepProps) {
  const [isWritingOther, setIsWritingOther] = useState(false);

  const [otherError, setOtherError] = useState<string | null>(null);

  const { card, brief } = draft;

  const currentKey = draft.draft?.optionKey ?? null;

  const submitOther = async (other: string) => {
    setOtherError(null);

    const isSelected = await onSelect({ other });

    if (!isSelected) {
      setOtherError(REPLY_DRAFT_COPY.otherFailed);
    }

    return isSelected;
  };

  const otherRow = isWritingOther ? null : (
    <Pressable
      onPress={() => setIsWritingOther(true)}
      disabled={isSelecting}
      testID="reply-draft-other-open"
      className={DECISION_OPTION_CLASS}
      {...getAccessibilityProps({ role: 'button', label: REPLY_DRAFT_COPY.somethingElseRow })}>
      <HStack className="items-center gap-3">
        <Icon as={Pencil} size="sm" className="text-typography-700" />
        <Text size="md" className="flex-1 text-typography-900">
          {REPLY_DRAFT_COPY.somethingElseRow}
        </Text>
      </HStack>
    </Pressable>
  );

  return (
    <VStack className="gap-5" testID="reply-draft-decide">
      <VStack className="gap-2">
        {card ? (
          <Heading ref={headingRef} size="h2-lg" className="font-semibold text-typography-900">
            {card.question}
          </Heading>
        ) : null}
        {brief ? <CaseDetails facts={brief.caseFacts} /> : null}
      </VStack>

      {brief ? (
        <ReplyDraftTourZone segment="decide" zone={REPLY_DRAFT_TOUR_ZONES.decide.research}>
          <VStack className="gap-1" testID="reply-draft-research">
            <SectionHeading>{REPLY_DRAFT_COPY.researchHeading}</SectionHeading>
            <VStack className="border-b border-outline-200">
              <ChangedRecently items={brief.changedRecently} />
              <SimilarCases history={brief.history} caseCount={brief.historyCaseCount} />
              {brief.peerSummary ? <PeerSummary summary={brief.peerSummary} /> : null}
            </VStack>
          </VStack>
        </ReplyDraftTourZone>
      ) : null}

      <VStack className="gap-3">
        <Text size="md" className="text-typography-900">
          {REPLY_DRAFT_COPY.chooseInstruction}
        </Text>

        <ReplyDraftTourZone segment="decide" zone={REPLY_DRAFT_TOUR_ZONES.decide.options}>
          <DecisionCard
            options={card?.options ?? []}
            isDisabled={isSelecting}
            currentKey={currentKey}
            onSelect={(optionKey) => {
              if (optionKey === currentKey) {
                onKeep();

                return;
              }

              onSelect({ optionKey }).catch(() => undefined);
            }}
            trailing={otherRow}
          />
        </ReplyDraftTourZone>

        {isWritingOther ? (
          <TextEntry
            label={REPLY_DRAFT_COPY.somethingElse}
            placeholder={REPLY_DRAFT_COPY.somethingElsePlaceholder}
            submitLabel={REPLY_DRAFT_COPY.somethingElseSubmit}
            busyLabel={REPLY_DRAFT_COPY.writing}
            isBusy={isSelecting}
            error={otherError}
            testID="reply-draft-other"
            autoFocus
            onSubmit={submitOther}
          />
        ) : null}
      </VStack>
    </VStack>
  );
}
